/* eslint-disable react/prop-types -- the rule cannot see through the imported GenericDataTableProps */
import { UploadOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { lazy, Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GenericDataTable,
  getStoredTableState,
  storeTableState,
  type GenericDataTableProps,
  type TableRow,
} from '@konnecta/generic-table';
import { useTableEmptyState } from '@hooks/useTableEmptyState';
import type { ExportListKey } from '@app/features/exports/exportLists';
import type { ImportListKey } from '@app/features/imports/importLists';
import { mergeColumnOrder, mergeColumnVisibility } from './mergeColumnLayout';

// Lazy: the dialog pulls in the API clients, which every table (and its tests) would otherwise load.
const ExportDialog = lazy(() => import('@app/features/exports/ExportDialog'));
const ImportDialog = lazy(() => import('@app/features/imports/ImportDialog'));

export type AppExportConfig = {
  list: ExportListKey;
  getParams: () => Record<string, unknown>;
  /** Column ids that never go into the file: actions, selection, pure UI, or ones the backend omits. */
  notExported?: string[];
};

export type AppImportConfig = {
  list: ImportListKey;
  /** Refetch the list after a committed import. */
  onImported: () => void;
};

export interface AppDataTableProps<TApi, TRow extends TableRow> extends GenericDataTableProps<TApi, TRow> {
  export?: AppExportConfig;
  import?: AppImportConfig;
}

type TableStateChange = NonNullable<GenericDataTableProps<unknown, TableRow>['onTableStateChange']>;

/**
 * Columns the table appends after a saved order that predates them (GenericTable's
 * orderedColumns) but leaves out of the visibleColumns it reports. Mirrors its hydration:
 * stored layout over config.initialState over the column defaults.
 */
const appendedColumnIds = <TApi, TRow extends TableRow>(config: GenericDataTableProps<TApi, TRow>['config']) => {
  const persisted = config.storage?.enableLocalStorage ?? true;
  const stored = persisted
    ? getStoredTableState(config.tableId, config.storage?.namespace, config.storage?.storageVersion)
    : null;
  const order = stored?.columnOrder?.orderedIds ?? config.initialState?.columnOrder?.orderedIds ?? [];
  if (order.length === 0) return [];
  const visibility = stored?.columnVisibility ?? config.initialState?.columnVisibility;
  const known = new Set(order);
  return config.columns
    .filter((c) => !known.has(c.id) && (visibility ? visibility[c.id] !== false : c.isVisible !== false))
    .map((c) => c.id);
};

/**
 * The table restores a saved layout over everything else and appends columns it has never
 * seen, so rewrite the saved layout first: new columns land beside their neighbours, with
 * their default visibility.
 */
const reconcileStoredLayout = <TApi, TRow extends TableRow>(config: GenericDataTableProps<TApi, TRow>['config']) => {
  if (!(config.storage?.enableLocalStorage ?? true)) return;
  const { namespace, storageVersion } = config.storage ?? {};
  const stored = getStoredTableState(config.tableId, namespace, storageVersion);
  if (!stored) return;
  const orderedIds = stored.columnOrder?.orderedIds;
  const mergedOrder = mergeColumnOrder(
    orderedIds,
    config.columns.map((c) => c.id)
  );
  const mergedVisibility = mergeColumnVisibility(
    stored.columnVisibility,
    config.columns,
    config.initialState?.columnVisibility
  );
  const orderChanged = mergedOrder?.join('|') !== orderedIds?.join('|');
  if (!orderChanged && mergedVisibility === stored.columnVisibility) return;
  storeTableState(
    config.tableId,
    {
      ...stored,
      ...(mergedOrder && { columnOrder: { orderedIds: mergedOrder } }),
      ...(mergedVisibility && { columnVisibility: mergedVisibility }),
    },
    namespace,
    storageVersion
  );
};

/**
 * GenericDataTable with translated empty-state text. Anything the call site sets
 * itself, as the `emptyState` prop or in `config.emptyState`, still wins.
 * `export` replaces the toolbar's export button with the list-export dialog.
 * `import` adds an Import button that opens the list-import dialog.
 */
export const AppDataTable = <TApi, TRow extends TableRow>({
  config,
  emptyState,
  export: exportConfig,
  import: importConfig,
  rightContent,
  onTableStateChange,
  ...rest
}: AppDataTableProps<TApi, TRow>) => {
  const { t } = useTranslation();
  const [importOpen, setImportOpen] = useState(false);
  const defaults = useTableEmptyState<TRow>();
  const columnIdsKey = config.columns.map((c) => c.id).join('|');
  // During render, so it lands before the table reads storage while mounting.
  useMemo(() => reconcileStoredLayout(config), [config.tableId, columnIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const ownEmptyState = emptyState ?? config.emptyState;
  // A fresh object every render would defeat the table's own memoization.
  const merged = useMemo(() => ({ ...defaults, ...ownEmptyState }), [defaults, ownEmptyState]);

  // Visible ids in the table's current order; empty until the table first reports.
  const visibleRef = useRef<string[]>([]);
  const handleTableState = useCallback<TableStateChange>(
    (state) => {
      visibleRef.current = state.visibleColumns;
      onTableStateChange?.(state);
    },
    [onTableStateChange]
  );

  // A string key so an inline notExported array doesn't rebuild the config every render.
  const notExportedKey = exportConfig?.notExported?.join('|') ?? '';
  const getExportColumns = useCallback(() => {
    const byId = new Map(config.columns.map((c) => [c.id, c]));
    const skip = new Set(notExportedKey.split('|'));
    const visible = visibleRef.current.length ? visibleRef.current : config.columns.map((c) => c.id);
    const ids = [...visible, ...appendedColumnIds(config).filter((id) => !visible.includes(id))];
    return ids.filter((id) => byId.has(id) && !skip.has(id)).map((id) => ({ key: id, header: byId.get(id)!.header }));
  }, [config, notExportedKey]);

  const list = exportConfig?.list;
  const getParams = exportConfig?.getParams;
  const effectiveConfig = useMemo(() => {
    if (!list || !getParams) return config;
    return {
      ...config,
      exportButton: {
        ...config.exportButton,
        mode: 'custom' as const,
        text: config.exportButton?.text ?? t('table-export'),
        // The table's icon default is a borderless large button; match the outlined columns/reset buttons.
        buttonProps: { type: 'default' as const, size: 'middle' as const, ...config.exportButton?.buttonProps },
        onClick: undefined,
        render: ({ close }: { close: () => void }) => (
          <Suspense fallback={null}>
            <ExportDialog list={list} getColumns={getExportColumns} getParams={getParams} onClose={close} />
          </Suspense>
        ),
      },
    };
  }, [config, list, getParams, getExportColumns, t]);

  const importButton = importConfig && (
    <>
      <div title={t('table-import')} className="flex items-center">
        <Button icon={<UploadOutlined />} aria-label={t('table-import')} onClick={() => setImportOpen(true)} />
      </div>
      {importOpen && (
        <Suspense fallback={null}>
          <ImportDialog
            list={importConfig.list}
            onClose={() => setImportOpen(false)}
            onImported={importConfig.onImported}
          />
        </Suspense>
      )}
    </>
  );

  return (
    <GenericDataTable<TApi, TRow>
      config={effectiveConfig}
      rightContent={rightContent}
      actionsContent={importButton}
      emptyState={merged}
      onTableStateChange={handleTableState}
      {...rest}
    />
  );
};
